import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, Calculator, Lightbulb } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import MathText from '@/components/MathText';
import { trackPracticeStart, trackPracticeComplete, trackHintUsed, trackCalculatorOpened } from '@/utils/analytics';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function PracticeSession() {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { sessionId, questions: initialQuestions, mode, topic, trainingTaskId, returnPath } = location.state || {};
  const backPath = returnPath || '/dashboard';
  
  const [questions, setQuestions] = useState(initialQuestions || []);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [pageTitle, setPageTitle] = useState('');
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorType, setCalculatorType] = useState('scientific');
  const [hints, setHints] = useState({});
  const [showHint, setShowHint] = useState({});

  // Set page title
  useEffect(() => {
    const modeTitle = mode === 'concept_quiz' ? 'Math Concept Quiz' : 'Practice Questions';
    const TOPIC_DISPLAY = {
      'math_all': 'Math',
      'rw_all': 'Reading & Writing',
      'Algebra': 'Algebra',
      'Advanced Math': 'Advanced Math',
      'Geometry & Trigonometry': 'Geometry & Trigonometry',
      'Problem Solving & Data Analysis': 'Problem Solving & Data Analysis',
      'Information and Ideas': 'Information and Ideas',
      'Craft and Structure': 'Craft and Structure',
      'Expression of Ideas': 'Expression of Ideas',
      'Standard English Conventions': 'Standard English Conventions'
    };
    const displayTopic = TOPIC_DISPLAY[topic] || topic || 'SAT Practice';
    document.title = `${modeTitle} - ${displayTopic} | Practice4SAT`;
    setPageTitle(`${modeTitle} - ${displayTopic}`);
  }, [mode, topic]);

  // Redirect if no questions
  useEffect(() => {
    if (!questions || questions.length === 0) {
      navigate(backPath);
    } else {
      // Debug: Check if questions have hints
      
      // Track practice session start
      if (mode && topic) {
        const difficulty = questions[0]?.difficulty || 'mixed';
        trackPracticeStart(mode, topic, difficulty);
      }
    }
  }, [questions, navigate, mode, topic]);

  // Timer removed - practice and concept quiz are untimed

  const handleExit = () => {
    navigate(backPath);
  };

  const handleAnswer = (answer) => {
    const currentQuestion = questions[currentQuestionIndex];
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    
    setAnswers({
      ...answers,
      [currentQuestion.id]: { answer, time_spent: timeSpent }
    });
  };

  const goToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setQuestionStartTime(Date.now());
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      goToQuestion(currentQuestionIndex + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      goToQuestion(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async (autoSubmit = false) => {
    setLoading(true);
    
    try {
      const submission = {
        answers: questions.map(q => ({
          question_id: q.id,
          answer: answers[q.id]?.answer || '',
          time_spent: answers[q.id]?.time_spent || 0
        }))
      };

      if (sessionId) {
        const response = await axios.post(`${API}/practice/sessions/${sessionId}/complete`, submission);
        
        // Track practice completion
        trackPracticeComplete(
          mode,
          response.data.score || 0,
          response.data.total_questions || questions.length,
          0  // No time tracking for practice sessions
        );
        
        // Mark training task as complete if this was from the training plan
        if (trainingTaskId) {
          try {
            await axios.post(`${API}/training-plan/task/${trainingTaskId}/complete`);
          } catch (taskError) {
            console.error('Error marking training task complete:', taskError);
          }
        }
        
        // Navigate to results with session settings for "Practice Again"
        navigate('/practice/results', {
          state: {
            sessionId: sessionId,
            mode: mode,
            topic: topic,
            returnPath: backPath,
            ...response.data
          }
        });
      } else {
        navigate(backPath);
        toast.success('Practice completed!');
      }
    } catch (error) {
      console.error('Error submitting practice:', error);
      toast.error('Failed to submit practice session');
      navigate(backPath);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!questions || questions.length === 0) {
    return null;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestion?.id];

  // Exit button for header
  const exitButton = (
    <Button 
      onClick={() => setShowExitDialog(true)}
      variant="outline"
      style={{ 
        background: 'white',
        color: '#0030b3',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}
    >
      <X size={20} />
      Exit Practice
    </Button>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header 
        variant="minimal" 
        onLogoClick={() => setShowExitDialog(true)}
        rightContent={exitButton}
      />

      {/* Main Content - EXACT SAME AS TEST INTERFACE */}
      <div style={{ flex: 1, background: '#f5f5f5' }}>
        <div style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 2rem' }}>
          {/* Title */}
          <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#0030b3', marginBottom: '1rem', textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
            {pageTitle}
          </h1>

          <div style={{ background: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          {/* Question Navigation */}
          <div style={{ marginBottom: '2rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {questions.map((q, idx) => (
              <button
                key={q.id}
                onClick={() => goToQuestion(idx)}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '4px',
                  border: `2px solid ${idx === currentQuestionIndex ? '#0030b3' : '#ddd'}`,
                  background: answers[q.id] ? '#0030b3' : 'white',
                  color: answers[q.id] ? 'white' : (idx === currentQuestionIndex ? '#0030b3' : '#666'),
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {idx + 1}
              </button>
            ))}
          </div>

          {/* Question */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0, fontFamily: 'Arial, sans-serif' }}>
                Question {currentQuestionIndex + 1}
              </h3>
              {currentQuestion?.difficulty && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  textTransform: 'capitalize',
                  background: currentQuestion.difficulty === 'easy' ? '#dcfce7' :
                             currentQuestion.difficulty === 'medium' ? '#fef3c7' : '#fee2e2',
                  color: currentQuestion.difficulty === 'easy' ? '#166534' :
                         currentQuestion.difficulty === 'medium' ? '#92400e' : '#991b1b'
                }}>
                  {currentQuestion.difficulty}
                </span>
              )}
            </div>
            <div style={{ fontSize: '1rem', lineHeight: '1.6', marginBottom: '1.5rem' }}>
              <MathText>{currentQuestion?.question_text}</MathText>
            </div>

            {(currentQuestion?.image_url || currentQuestion?.image) && (
              <img 
                src={currentQuestion.image_url || currentQuestion.image} 
                alt="Question diagram" 
                style={{ 
                  maxWidth: 'min(400px, 100%)', 
                  width: 'auto',
                  height: 'auto',
                  marginBottom: '1.5rem', 
                  borderRadius: '4px',
                  display: 'block'
                }}
              />
            )}
          </div>

          {/* Answer Options - Multiple Choice */}
          {currentQuestion?.choices && (
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {currentQuestion.choices.map((choice) => (
                  <button
                    key={choice}
                    onClick={() => handleAnswer(choice)}
                    style={{
                      padding: '1rem',
                      borderRadius: '8px',
                      border: `2px solid ${currentAnswer?.answer === choice ? '#0030b3' : '#ddd'}`,
                      background: currentAnswer?.answer === choice ? '#e6f0ff' : 'white',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (currentAnswer?.answer !== choice) {
                        e.currentTarget.style.borderColor = '#999';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentAnswer?.answer !== choice) {
                        e.currentTarget.style.borderColor = '#ddd';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ fontWeight: '600', marginRight: '0.75rem', color: '#333' }}>{choice}</span>
                      <div style={{ flex: 1 }}>
                        <MathText>{currentQuestion.choice_texts?.[choice]}</MathText>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Answer Input - Student Produced Response */}
          {currentQuestion?.question_type === 'student_produced_response' && (
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', fontFamily: 'Arial, sans-serif' }}>
                Your Answer:
              </label>
              <input
                type="text"
                value={currentAnswer?.answer || ''}
                onChange={(e) => handleAnswer(e.target.value)}
                style={{
                  width: '200px',
                  padding: '0.75rem',
                  fontSize: '1.1rem',
                  border: '2px solid #ddd',
                  borderRadius: '4px',
                  fontWeight: '600'
                }}
                placeholder="Enter answer"
              />
            </div>
          )}

          {/* Navigation Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e5e5' }}>
            <Button
              onClick={prevQuestion}
              disabled={currentQuestionIndex === 0}
              variant="outline"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <ChevronLeft size={20} />
              Previous
            </Button>
            
            {currentQuestionIndex === questions.length - 1 ? (
              <Button
                onClick={() => setShowSubmitDialog(true)}
                style={{
                  background: '#0030b3',
                  color: 'white'
                }}
              >
                Submit Practice
              </Button>
            ) : (
              <Button
                onClick={nextQuestion}
                style={{
                  background: '#0030b3',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                Next
                <ChevronRight size={20} />
              </Button>
            )}
          </div>

          {/* Hint Button - Only for Practice Mode */}
          {mode === 'practice' && (
            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e5e5' }}>
              <Button
                onClick={() => {
                  const currentQ = questions[currentQuestionIndex];
                  
                  // Track hint usage (only on first view)
                  if (!showHint[currentQ.id]) {
                    trackHintUsed(currentQ.id, mode);
                  }
                  
                  // Always set both states together
                  const hintText = currentQ.hint || "Try breaking down the problem step by step. Look for key information in the question and think about what formula or concept applies.";
                  
                  // Use functional setState to ensure we get the latest state
                  setShowHint(prev => {
                    const newState = { ...prev, [currentQ.id]: true };
                    return newState;
                  });
                  
                  setHints(prev => {
                    const newState = { ...prev, [currentQ.id]: hintText };
                    return newState;
                  });
                  
                }}
                variant="outline"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: showHint[currentQuestion?.id] ? '#fef3c7' : 'white',
                  borderColor: '#f59e0b'
                }}
              >
                <Lightbulb size={20} color="#f59e0b" />
                {showHint[currentQuestion?.id] ? 'Hint Shown' : 'Show Hint'}
              </Button>

              {/* Display Hint */}
              {showHint[currentQuestion?.id] && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: '#fef3c7',
                  border: '2px solid #f59e0b',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '0.75rem'
                }}>
                  <Lightbulb size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: '0.1rem' }} />
                  <div style={{ fontSize: '0.95rem', color: '#78350f', lineHeight: '1.5' }}>
                    {(() => {
                      const hintText = hints[currentQuestion?.id];
                      const questionHint = currentQuestion?.hint;
                      
                      // Try getting hint from either hints state OR directly from question
                      const displayHint = hintText || questionHint;
                      
                      if (displayHint) {
                        return (
                          <>
                            <strong>Hint:</strong>{' '}
                            <span 
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(displayHint
                                  .replace(/\\\\?\(/g, '<span class="math-inline">\\(')
                                  .replace(/\\\\?\)/g, '\\)</span>'))
                              }}
                              ref={(el) => {
                                if (el && window.MathJax) {
                                  window.MathJax.typesetPromise([el]).catch((err) => console.error('MathJax error:', err));
                                }
                              }}
                            />
                          </>
                        );
                      } else {
                        return (
                          <>
                            <strong>Hint:</strong> <span style={{color: '#ef4444'}}>No hint available</span>
                          </>
                        );
                      }
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          </div>
        </div>
      </div>

      {/* Floating Calculator Button */}
      <button
        onClick={() => {
          setShowCalculator(true);
          trackCalculatorOpened(calculatorType);
        }}
        style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s, box-shadow 0.2s',
          zIndex: 999
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
        }}
        title="Open Calculator"
      >
        <Calculator size={28} color="white" />
      </button>

      {/* Calculator Modal */}
      <Dialog open={showCalculator} onOpenChange={setShowCalculator}>
        <DialogContent style={{ 
          maxWidth: 'min(900px, 95vw)', 
          width: '95vw',
          height: 'min(700px, 85vh)', 
          maxHeight: '85vh',
          padding: 0,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <DialogHeader style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
            <DialogTitle>Desmos Calculator</DialogTitle>
            <DialogDescription>
              Choose between scientific or graphing calculator
            </DialogDescription>
            {/* Calculator Type Tabs */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button
                onClick={() => setCalculatorType('scientific')}
                style={{
                  padding: '0.5rem 1.5rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: calculatorType === 'scientific' ? '#667eea' : '#e5e7eb',
                  color: calculatorType === 'scientific' ? 'white' : '#666',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Scientific
              </button>
              <button
                onClick={() => setCalculatorType('graphing')}
                style={{
                  padding: '0.5rem 1.5rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: calculatorType === 'graphing' ? '#667eea' : '#e5e7eb',
                  color: calculatorType === 'graphing' ? 'white' : '#666',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Graphing
              </button>
            </div>
          </DialogHeader>
          <div style={{ width: '100%', flex: 1, minHeight: 0 }}>
            <iframe
              src={calculatorType === 'scientific' 
                ? 'https://www.desmos.com/scientific' 
                : 'https://www.desmos.com/calculator'}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title={`Desmos ${calculatorType === 'scientific' ? 'Scientific' : 'Graphing'} Calculator`}
              key={calculatorType}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Footer />

      {/* Submit Confirmation Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Practice Session?</DialogTitle>
            <DialogDescription>
              Are you sure you want to submit? You have answered {Object.keys(answers).length} out of {questions.length} questions.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleSubmit()} style={{ background: '#0030b3', color: 'white' }}>
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exit Confirmation Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exit Practice?</DialogTitle>
            <DialogDescription>
              Your progress will not be saved. Are you sure you want to exit?
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setShowExitDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleExit} variant="destructive">
              Exit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
